import React, { useEffect, useState } from "react";
import { CalendarClock, Package, Plus, Trash2, AlertTriangle } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { getClientFieldOptions } from "../lib/clientFieldOptions.js";
import { toast } from "./Toast.jsx";
import { ZalozitOkno, FAKTURACE_ZMENA } from "./PlanFakturaceSection.jsx";
import { Stitek } from "./ui.jsx";

// "Naplánovat změnu balíčku" — malá sekce v kartě Spolupráce (detail klienta).
// Umožní naplánovat přepnutí balíčku k budoucímu datu. Denní cron
// (scheduled-planovane-zmeny-baliku) změnu v daný den provede přes klient-edit
// (zapíše Notion + Sheet + PG a vytvoří notifikaci o úpravě fakturace ve FAPI).
//
// Props:
//   klientId      — notion_id klienta (klient_id)
//   notionPageId  — notion page id (job jím volá klient-edit)
//   klientName    — jméno klienta (pro přehled)
//   readOnly      — když true, jen zobrazí (bez formu)
//
// 19. 10. 2026 (Láďa): změna na koučink u klientky bez pravidelné faktury (typicky z permanentky)
// nabídne rovnou Založit pravidelnou fakturu s prvním dnem = den změny (Plán fakturace).
const JE_KOUCINK = /Koučink (FLEXIBLE|GOLD|PREMIUM) [12]T/;

const FALLBACK_BALICKY = [
  "Koučink FLEXIBLE 1T",
  "Koučink FLEXIBLE 2T",
  "Koučink GOLD 1T",
  "Koučink GOLD 2T",
  "Koučink GOLD 3T",
  "Koučink PREMIUM 1T",
  "Koučink PREMIUM 2T",
];

// 19. 10. 2026: datum v pilulkách stejně jako v Plánu fakturace (2. 11. 2026).
function fmtCz(ds) {
  const m = String(ds || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${Number(m[3])}. ${Number(m[2])}. ${m[1]}` : "";
}

function fmtDay(ds) {
  if (!ds) return "—";
  return String(ds).substring(0, 10).split("-").reverse().join(".");
}

export default function PlanovanaZmenaBalickuSection({ klientId, notionPageId, klientName, readOnly = false }) {
  const [zmena, setZmena] = useState(null); // aktuální naplánovaná změna | null
  const [loading, setLoading] = useState(true);
  const [balicky, setBalicky] = useState(FALLBACK_BALICKY);
  const [novyBalicek, setNovyBalicek] = useState("");
  const [datum, setDatum] = useState("");
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [fakturace, setFakturace] = useState(null); // { maFakturu, naplanovano } z Plánu fakturace
  const [zalozit, setZalozit] = useState(false);

  // min = zítra (lokální kalendářní den prohlížeče = Praha).
  const tomorrow = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toLocaleDateString("sv-SE");
  })();

  const idParam = klientId
    ? `klient_id=${encodeURIComponent(klientId)}`
    : (notionPageId ? `notion_page_id=${encodeURIComponent(notionPageId)}` : "");

  const load = async () => {
    if (!idParam) { setLoading(false); return; }
    try {
      const r = await apiFetch(`/api/klient-planovana-zmena?${idParam}`);
      const j = await r.json();
      // FW-145: endpoint vrací i plánované ukončení (akce 'ukonceni'), to má vlastní sekci.
      const arr = (Array.isArray(j.zmeny) ? j.zmeny : []).filter((z) => (z.akce || "balicek") === "balicek");
      setZmena(arr.length > 0 ? arr[0] : null);
    } catch (_) { /* ticho */ }
    setLoading(false);
  };

  useEffect(() => {
    let alive = true;
    getClientFieldOptions().then((opts) => {
      if (alive && Array.isArray(opts?.balicek) && opts.balicek.length > 0) setBalicky(opts.balicek);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const nactiFakturaci = () => {
    if (!klientId) return;
    apiFetch(`/api/fakturace-plan?klient=${encodeURIComponent(klientId)}`).then((r) => r.json()).then((j) => {
      if (!j || !j.ok) return;
      setFakturace({
        maFakturu: (j.pravidelne || []).some((f) => f.status === "active" || f.status === "suspended"),
        naplanovano: (j.zalozeni || [])[0] || null,
      });
    }).catch(() => null);
  };
  useEffect(() => {
    nactiFakturaci();
    window.addEventListener(FAKTURACE_ZMENA, nactiFakturaci);
    return () => window.removeEventListener(FAKTURACE_ZMENA, nactiFakturaci);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [klientId]);

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idParam]);

  const doSet = async () => {
    if (!novyBalicek) { toast("Vyber nový balíček", "warning"); return; }
    if (!datum) { toast("Vyber datum změny", "warning"); return; }
    if (datum < tomorrow) { toast("Datum musí být nejdříve zítra", "warning"); return; }
    setSaving(true);
    try {
      const r = await apiFetch("/api/klient-planovana-zmena", {
        method: "POST",
        body: JSON.stringify({
          action: "set",
          klient_id: klientId || undefined,
          klient_name: klientName || undefined,
          notion_page_id: notionPageId,
          novy_balicek: novyBalicek,
          datum,
        }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j.error || "Uložení selhalo");
      toast("Změna balíčku naplánována", "success");
      setNovyBalicek("");
      setDatum("");
      await load();
    } catch (e) {
      toast(e.message || "Uložení selhalo", "error");
    }
    setSaving(false);
  };

  const doCancel = async () => {
    if (!zmena) return;
    setCancelling(true);
    try {
      const r = await apiFetch("/api/klient-planovana-zmena", {
        method: "POST",
        body: JSON.stringify({ action: "cancel", id: zmena.id }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j.error || "Zrušení selhalo");
      toast("Naplánovaná změna zrušena", "success");
      setZmena(null);
    } catch (e) {
      toast(e.message || "Zrušení selhalo", "error");
    }
    setCancelling(false);
  };

  if (!notionPageId && !klientId) return null;

  return (
    <div className="mt-4 pt-4 border-t border-slate-200">
      <div className="flex items-center gap-2 mb-3">
        <CalendarClock className="w-4 h-4 text-plum-500" />
        <h4 className="text-sm font-semibold text-slate-800">Naplánovat změnu balíčku</h4>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400">Načítám…</div>
      ) : zmena ? (
        <div className="flex items-start gap-3 p-3 rounded-xl bg-plum-50 border border-plum-200">
          <Package className="w-4 h-4 mt-0.5 text-plum-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-800">Naplánovaná změna balíčku</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              <Stitek ton="neutralni" velikost="mala">{zmena.novy_balicek}</Stitek>
              <Stitek ton="neutralni" velikost="mala">od {fmtCz(zmena.datum)}</Stitek>
              {fakturace && JE_KOUCINK.test(zmena.novy_balicek) && (fakturace.maFakturu
                ? <Stitek ton="ok" velikost="mala">Pravidelná faktura běží</Stitek>
                : fakturace.naplanovano
                  ? <Stitek ton="ok" velikost="mala">Faktura se založí {fmtCz(fakturace.naplanovano.prvni)}</Stitek>
                  : <Stitek ton="pozor" velikost="mala">Bez pravidelné faktury</Stitek>)}
            </div>
            <div className="text-xs text-slate-500 mt-1.5">
              {!fakturace || !JE_KOUCINK.test(zmena.novy_balicek)
                ? "Změna se provede automaticky. Nezapomeň pak upravit fakturaci ve FAPI."
                : fakturace.maFakturu
                  ? "Změna se provede automaticky. Pravidelnou fakturu uprav v Plánu fakturace (Upravit fakturu)."
                  : fakturace.naplanovano
                    ? "Změna se provede automaticky a ráno se vystaví první faktura."
                    : "Změna se provede automaticky. Pravidelnou fakturu založ od dne změny."}
            </div>
            {fakturace && JE_KOUCINK.test(zmena.novy_balicek) && !fakturace.maFakturu && !fakturace.naplanovano && !readOnly && (
              <button type="button" className="btn btn-primary mt-2 !min-h-0 whitespace-nowrap !py-1.5 text-sm" onClick={() => setZalozit(true)}>
                <Plus className="w-4 h-4" /> Založit pravidelnou fakturu
              </button>
            )}
            {zmena.chyba && (
              <div className="text-xs text-rose-600 mt-1 inline-flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Poslední pokus selhal: {zmena.chyba}
              </div>
            )}
          </div>
          {!readOnly && (
            <button
              onClick={doCancel}
              disabled={cancelling}
              title="Zrušit naplánovanou změnu"
              aria-label="Zrušit naplánovanou změnu"
              className="inline-flex justify-center min-w-[44px] sm:min-w-0 items-center gap-1.5 text-xs font-medium text-rose-600 border border-rose-200 bg-white rounded-xl px-2.5 py-1.5 hover:bg-rose-50 disabled:opacity-50 flex-shrink-0 min-h-[44px] sm:min-h-0"
            >
              <Trash2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{cancelling ? "Ruším…" : "Zrušit"}</span>
            </button>
          )}
        </div>
      ) : readOnly ? (
        <div className="text-sm text-slate-400">Žádná naplánovaná změna.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
          <label className="block text-sm">
            <div className="text-xs text-slate-500 mb-1">Nový balíček</div>
            <select
              value={novyBalicek}
              onChange={(e) => setNovyBalicek(e.target.value)}
              disabled={saving}
              className="field w-full disabled:opacity-50"
            >
              <option value="">Vyber balíček</option>
              {balicky.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <div className="text-xs text-slate-500 mb-1">Datum změny</div>
            <input
              type="date"
              value={datum}
              min={tomorrow}
              onChange={(e) => setDatum(e.target.value)}
              disabled={saving}
              className="field w-full disabled:opacity-50"
            />
          </label>
          <button
            onClick={doSet}
            disabled={saving || !novyBalicek || !datum}
            className="btn btn-primary"
          >
            <CalendarClock className="w-4 h-4" /> {saving ? "Ukládám…" : "Naplánovat"}
          </button>
        </div>
      )}
      {zalozit && zmena && (
        <ZalozitOkno klientId={klientId} vychozi={{ balicek: zmena.novy_balicek, datum: String(zmena.datum).substring(0, 10) }}
          onClose={() => setZalozit(false)} onHotovo={() => setZalozit(false)} />
      )}
    </div>
  );
}
